import { cn } from '@/shared/helpers/classnames'
import { type DataAttributes } from '@/shared/types/html'

import { type FC, type ReactNode } from 'react'

import { Tag, type TagProps } from './Tag'

/**
 * Props for the TagArray component
 *
 * @typedef {Object} TagArrayProps
 * @property {number} [maxTagsDisplayed] - Maximum number of tags to display before showing the "more" indicator. If undefined, all tags are displayed.
 * @property {T[]} tags - Array of tag configurations to render
 * @property {Partial<T>} [defaultProps] - Default properties to apply to all tags (can be overridden by individual tag props)
 * @property {(countMoreItems: number) => string} [moreLabel] - Function to generate the label for additional tags indicator. Defaults to "+{count}" format.
 * @property {FC<T>} [component] - Custom component to render the tags. Receives tag props and should return a ReactNode.
 */
export type TagArrayProps<T = TagProps> = {
  maxTagsDisplayed?: number
  tags: T[]
  defaultProps?: Partial<T>
  moreTagProps?: Partial<T>
  moreLabel?: (countMoreItems: number) => string
  moreTooltip?: ({ tags }: { tags: T[] }) => ReactNode
  component?: FC<T> | null
} & DataAttributes

export type MoreTagProps<T = TagProps> = T & Pick<TagArrayProps<T>, 'tags' | 'moreTooltip'>

/**
 * The Tag component displays a collection of fund badges with optional label and currency symbols.
 * Each fund is rendered as a badge with customizable style variants.
 *
 * @param {TagArrayProps<T>} props - TagArray component props
 * @returns {JSX.Element}
 */
export function TagArray<T extends Record<string, unknown> = TagProps>({
  tags,
  maxTagsDisplayed,
  defaultProps,
  moreTagProps,
  moreLabel = (countMoreItems: number) => `+${countMoreItems}`,
  moreTooltip = ({ tags }: { tags: T[] }) =>
    tags.map(tag => (tag as TagProps).label ?? '').join('\n'),
  component,
  ...dataProps
}: TagArrayProps<T>): ReactNode {
  const TagsComponent = component ?? (Tag as FC<T>)
  const tagsToDisplay = maxTagsDisplayed === undefined ? tags : tags.slice(0, maxTagsDisplayed)
  const hasMoreTags = maxTagsDisplayed !== undefined && tags.length > maxTagsDisplayed

  const allMoreTagProps = hasMoreTags
    ? {
        ...defaultProps,
        icon: undefined,
        label: moreLabel(tags.length - maxTagsDisplayed),
        tooltip: { content: moreTooltip({ tags: tags.slice(maxTagsDisplayed) }) },
        ...moreTagProps,
      }
    : null

  return (
    <div {...dataProps} className={cn('inline-flex items-center gap-2')}>
      {tagsToDisplay.map((tag, index) => (
        <TagsComponent key={index} {...defaultProps} {...tag} />
      ))}
      {hasMoreTags && <TagsComponent {...(allMoreTagProps as unknown as T)} />}
    </div>
  )
}
